<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useDebounce } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import type { FieldSchema } from '../../../types';

const UNIT_KEYS = [
  'units.application',
  'units.can',
  'units.capsule',
  'units.cm',
  'units.cup',
  'units.dose',
  'units.drop(s)',
  'units.g',
  'units.g/kg',
  'units.gr/kg',
  'units.l/kg',
  'units.mcg',
  'units.mcgs',
  'units.mcgs/kg',
  'units.mg',
  'units.mg/kg',
  'units.mg/lb',
  'units.ml',
  'units.ml/hr',
  'units.ml/kg',
  'units.ml/lb',
  'units.oz',
  'units.package',
  'units.pinch',
  'units.pipette',
  'units.scoop',
  'units.spray',
  'units.tablet',
  'units.tablet/kg',
  'units.tblsp',
  'units.tsp',
  'units.unit',
  'units.units/kg',
];

const UNIT_LABEL_KEYS: Record<string, string> = {
  'units.drop(s)': 'units.drop',
};

export interface Model {
  amount: string;
  amount_unit: string;
}

defineProps<{
  disabled?: boolean;
  error?: string;
  field?: FieldSchema;
  placeholder?: string;
  required?: boolean;
}>();

const { t } = useI18n();

const model = defineModel<Model | undefined>();
const modelNumber = ref();
const modelUnit = ref();

const options = computed(() =>
  UNIT_KEYS.map((unitKey) => ({
    label: t(UNIT_LABEL_KEYS[unitKey] ?? unitKey),
    value: t(unitKey),
  }))
);

const input = useDebounce(modelNumber, 400);

onMounted(() => setModelsOnStart());

watch(input, () => numberInputRulesHandler());
watch([input, modelUnit], () => updateModel());
watch(model, setModelsOnStart);

function numberInputRulesHandler() {
  const value = modelNumber.value?.toString() || null;

  modelNumber.value = value;
}

function setModelsOnStart() {
  if (!model.value) {
    modelNumber.value = null;
    modelUnit.value = null;
    model.value = undefined;
    return;
  }

  modelNumber.value = model.value.amount || null;
  modelUnit.value = model.value?.amount_unit?.toLowerCase();
  numberInputRulesHandler();
}

function updateModel() {
  model.value = { amount: modelNumber.value, amount_unit: modelUnit.value };
}
</script>

<template>
  <div class="flex flex-col gap-3 sm:flex-row">
    <div class="w-full sm:!w-1/2">
      <AFieldInputText
        v-model="modelNumber"
        :disabled="field?.state?.disabled || disabled"
        :error
        :required
        :showButtons="false"
      />
    </div>

    <AFieldDropdown
      v-model="modelUnit"
      class="w-full sm:!w-1/2"
      optionLabel="label"
      optionValue="value"
      :disabled="field?.state?.disabled || disabled"
      :error
      :options
      :placeholder
      :required
      :showClear="!required"
      @change="updateModel"
    />
  </div>
</template>
